import React from "react";
import { X } from "lucide-react";
import { C } from "../../constants/design";
import { sb } from "../../lib/supabase";
import { useI18n } from "../../i18n/I18nContext";

const APP_VERSION = "beta";

type Category = "bug" | "idea" | "other";

const CATEGORIES: { key: Category; labelKey: string; emoji: string }[] = [
  { key: "bug", labelKey: "feedback.catBug", emoji: "🐞" },
  { key: "idea", labelKey: "feedback.catIdea", emoji: "💡" },
  { key: "other", labelKey: "feedback.catOther", emoji: "💬" },
];

export function FeedbackModal({
  profileId,
  playerName,
  currentTab,
  onClose,
}: {
  profileId?: string | null;
  playerName?: string | null;
  currentTab?: string;
  onClose: () => void;
}) {
  const { t } = useI18n();
  const [category, setCategory] = React.useState<Category>("bug");
  const [message, setMessage] = React.useState("");
  const [sending, setSending] = React.useState(false);
  const [sent, setSent] = React.useState(false);
  const [error, setError] = React.useState(false);

  const canSend = message.trim().length > 0 && !sending;

  const submit = async () => {
    if (!canSend) return;
    setSending(true);
    setError(false);
    // insertEvent never throws — it returns false and warns on failure (e.g. the
    // RLS migration not applied yet). We MUST honor that boolean: silently
    // confirming a lost submission is the worst outcome for a feedback tool.
    const ok = await sb.insertEvent("feedback", {
      profile_id: profileId ?? null,
      player_name: playerName ?? null,
      category,
      message: message.trim(),
      app_context: {
        tab: currentTab ?? null,
        url: typeof window !== "undefined" ? window.location.href : null,
        userAgent: typeof navigator !== "undefined" ? navigator.userAgent : null,
        appVersion: APP_VERSION,
      },
    });
    setSending(false);
    if (ok) {
      setSent(true);
      setTimeout(onClose, 1400);
    } else {
      setError(true);
    }
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 100,
        background: "rgba(4,8,14,0.72)",
        backdropFilter: "blur(4px)",
        display: "flex",
        alignItems: "flex-end",
        justifyContent: "center",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "100%",
          maxWidth: 460,
          background: C.surface,
          borderTopLeftRadius: 20,
          borderTopRightRadius: 20,
          border: `1px solid ${C.line}`,
          borderBottom: "none",
          padding: "18px 18px calc(20px + env(safe-area-inset-bottom))",
          boxShadow: "0 -10px 40px rgba(0,0,0,0.5)",
        }}
      >
        {/* Header */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 4 }}>
          <div style={{ fontWeight: 900, fontSize: 17, color: C.text }}>{t("feedback.title")}</div>
          <button
            onClick={onClose}
            style={{
              background: "rgba(255,255,255,0.06)",
              border: `1px solid ${C.line}`,
              borderRadius: 8,
              width: 30,
              height: 30,
              display: "grid",
              placeItems: "center",
              cursor: "pointer",
              color: C.muted,
            }}
          >
            <X size={16} />
          </button>
        </div>

        {sent ? (
          <div style={{ textAlign: "center", padding: "34px 0 20px" }}>
            <div style={{ fontSize: 40, marginBottom: 8 }}>🙌</div>
            <div style={{ fontWeight: 800, fontSize: 16, color: C.text }}>{t("feedback.thanksTitle")}</div>
            <div style={{ fontSize: 13, color: C.muted, marginTop: 4 }}>{t("feedback.thanksSub")}</div>
          </div>
        ) : (
          <>
            <div style={{ fontSize: 13, color: C.muted, marginBottom: 14 }}>
              {t("feedback.subtitle")}
            </div>

            {/* Category chips */}
            <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
              {CATEGORIES.map(({ key, labelKey, emoji }) => {
                const active = category === key;
                return (
                  <button
                    key={key}
                    onClick={() => setCategory(key)}
                    style={{
                      flex: 1,
                      padding: "9px 0",
                      borderRadius: 10,
                      cursor: "pointer",
                      fontWeight: 800,
                      fontSize: 13,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 5,
                      background: active ? `${C.turf}22` : "rgba(255,255,255,0.04)",
                      border: `1px solid ${active ? C.turf : C.line}`,
                      color: active ? C.turf : C.muted,
                      transition: "all 0.12s",
                    }}
                  >
                    <span>{emoji}</span>
                    {t(labelKey)}
                  </button>
                );
              })}
            </div>

            {/* Message */}
            <textarea
              autoFocus
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder={t("feedback.placeholder")}
              rows={4}
              maxLength={1000}
              style={{
                width: "100%",
                boxSizing: "border-box",
                resize: "vertical",
                minHeight: 96,
                padding: "11px 12px",
                borderRadius: 12,
                background: C.bg2,
                border: `1px solid ${C.line}`,
                color: C.text,
                fontSize: 14,
                lineHeight: 1.5,
                fontFamily: "inherit",
                outline: "none",
              }}
            />

            {error && (
              <div style={{ marginTop: 10, fontSize: 12.5, color: "#F87171", fontWeight: 600 }}>
                {t("feedback.error")}
              </div>
            )}

            <button
              onClick={submit}
              disabled={!canSend}
              style={{
                width: "100%",
                marginTop: 12,
                padding: "13px 0",
                borderRadius: 12,
                border: "none",
                cursor: canSend ? "pointer" : "not-allowed",
                fontWeight: 900,
                fontSize: 15,
                color: canSend ? "#04140A" : C.faint,
                background: canSend
                  ? `linear-gradient(135deg,${C.turf},${C.turfDim})`
                  : "rgba(255,255,255,0.05)",
                transition: "opacity 0.12s",
              }}
            >
              {sending ? t("feedback.sending") : t("feedback.send")}
            </button>
          </>
        )}
      </div>
    </div>
  );
}
